import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Archive,
  CalendarCheck,
  ChevronDown,
  ChevronUp,
  Clipboard,
  Eye,
  FileText,
  Pencil,
  PenLine,
  Plus,
  RefreshCw,
  Send,
  Trash,
  X,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { DiagramAnswer } from "@/features/forms/DiagramAnswer";
import { cn } from "@/lib/utils";
import { formatDate, formatRelative } from "@/lib/format";
import type {
  Contact, FormField, FormFieldType, FormFrequency, FormRequest, FormSection, FormTemplate, Service, ServiceCategory,
} from "@/lib/types";
import {
  Badge,
  Button,
  Drawer,
  EmptyState,
  FieldHint,
  Input,
  Label,
  Modal,
  ModalFooter,
  Select,
  Skeleton,
  Textarea,
  useConfirm,
  useToast,
} from "@/components/ui";

/* ─── Shared helpers ───────────────────────────────────────────────────────── */

const KIND_BADGE: Record<FormTemplate["kind"], { color: string; label: string }> = {
  intake:  { color: "blue",   label: "Intake" },
  consent: { color: "violet", label: "Consent" },
  custom:  { color: "zinc",   label: "Custom" },
};

const STATUS_BADGE: Record<FormRequest["status"], { color: string; label: string }> = {
  sent:      { color: "zinc",    label: "Sent" },
  viewed:    { color: "amber",   label: "Opened" },
  completed: { color: "emerald", label: "Completed" },
};

const FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  heading:     "Heading",
  statement:   "Statement",
  text:        "Short answer",
  textarea:    "Long answer",
  select:      "Dropdown",
  multiselect: "Checkboxes",
  checkbox:    "Single checkbox",
  date:        "Date",
  phone:       "Phone",
  email:       "Email",
  number:      "Number",
  // "forms for mark up" — a face she draws on. The `options` list carries
  // which drawing: face-front (the default) or face-side.
  diagram:     "Draw on a face",
};

/** Field types the client actually answers (everything except display-only). */
const INPUT_TYPES: FormFieldType[] = [
  "text", "textarea", "select", "multiselect", "checkbox", "date", "phone", "email", "number",
];

const PLACEHOLDER_TYPES: FormFieldType[] = ["text", "textarea", "phone", "email", "number"];

function errorMessage(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong";
}

function contactName(c: Contact): string {
  return (
    c.full_name ||
    [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
    c.email ||
    c.phone ||
    "Unnamed contact"
  );
}

function questionCount(fields: FormField[]): number {
  return fields.filter((f) => f.type !== "heading" && f.type !== "statement").length;
}

function renderAnswer(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.map(String).join(", ") : "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

/* ─── Page ─────────────────────────────────────────────────────────────────── */

export function FormsPage() {
  const [tab, setTab] = useState<"templates" | "sent">("templates");

  const [templates, setTemplates] = useState<FormTemplate[] | null>(null);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  const [editing, setEditing] = useState<FormTemplate | "new" | null>(null);
  const [linking, setLinking] = useState<FormTemplate | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  // Bumped whenever a request is created elsewhere so the Sent tab refetches.
  const [requestsVersion, setRequestsVersion] = useState(0);

  const loadTemplates = useCallback(async () => {
    setTemplatesError(null);
    try {
      const r = await api.get<{ templates: FormTemplate[] }>("/forms");
      setTemplates(r.templates);
    } catch (e) {
      setTemplatesError(errorMessage(e));
      setTemplates((prev) => prev ?? []);
    }
  }, []);

  useEffect(() => { void loadTemplates(); }, [loadTemplates]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Forms</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Intake and consent forms that send themselves with every booking.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            leftIcon={<Send className="h-4 w-4" />}
            onClick={() => setSendOpen(true)}
          >
            Send a form
          </Button>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>
            New form
          </Button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-zinc-800">
        {([
          ["templates", "Templates"],
          ["sent", "Sent"],
        ] as const).map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              "inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              tab === k
                ? "border-indigo-500 text-zinc-100"
                : "border-transparent text-zinc-500 hover:text-zinc-300"
            )}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "templates" && (
        <TemplatesTab
          templates={templates}
          error={templatesError}
          onRetry={() => { setTemplates(null); void loadTemplates(); }}
          onNew={() => setEditing("new")}
          onEdit={(t) => setEditing(t)}
          onLink={(t) => setLinking(t)}
          onChanged={loadTemplates}
        />
      )}

      {linking && (
        <FormServicesModal
          form={linking}
          onClose={() => setLinking(null)}
          onSaved={() => { setLinking(null); void loadTemplates(); }}
        />
      )}

      {tab === "sent" && (
        <SentTab reloadKey={requestsVersion} onChanged={() => void loadTemplates()} />
      )}

      {editing !== null && (
        <TemplateBuilder
          key={editing === "new" ? "new" : editing.id}
          template={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void loadTemplates(); }}
        />
      )}

      <SendFormDialog
        open={sendOpen}
        templates={(templates ?? []).filter((t) => !t.archived)}
        onClose={() => setSendOpen(false)}
        onSent={() => {
          setSendOpen(false);
          setRequestsVersion((v) => v + 1);
          void loadTemplates();
        }}
      />
    </div>
  );
}

/* ─── Templates tab ────────────────────────────────────────────────────────── */

function TemplatesTab({
  templates,
  error,
  onRetry,
  onNew,
  onEdit,
  onLink,
  onChanged,
}: {
  templates: FormTemplate[] | null;
  error: string | null;
  onRetry: () => void;
  onNew: () => void;
  onEdit: (t: FormTemplate) => void;
  onLink: (t: FormTemplate) => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const visible = useMemo(() => (templates ?? []).filter((t) => !t.archived), [templates]);

  const archive = async (t: FormTemplate) => {
    const ok = await confirm({
      title: `Archive "${t.name}"?`,
      description: "The form stops being available to send. Forms already sent keep working.",
      confirmLabel: "Archive",
      danger: true,
    });
    if (!ok) return;
    setArchivingId(t.id);
    try {
      await api.delete(`/forms/${t.id}`);
      toast.success("Form archived");
      onChanged();
    } catch (e) {
      toast.error("Couldn't archive form", errorMessage(e));
    } finally {
      setArchivingId(null);
    }
  };

  if (templates === null) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="mt-2 h-3.5 w-72" />
          </div>
        ))}
      </div>
    );
  }

  if (error && visible.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5 text-center">
        <p className="text-sm text-rose-400">{error}</p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }

  if (visible.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="No forms yet"
        description="Build your first intake or consent form and it can go out automatically with every booking."
        action={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={onNew}>
            New form
          </Button>
        }
      />
    );
  }

  return (
    <ul className="space-y-3">
      {visible.map((t) => {
        const kind = KIND_BADGE[t.kind] ?? KIND_BADGE.custom;
        const q = questionCount(t.fields);
        const sent = t.sent_count ?? 0;
        return (
          <li
            key={t.id}
            className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-zinc-100">{t.name}</h3>
                <Badge color={kind.color}>{kind.label}</Badge>
                {t.require_signature && (
                  <Badge color="amber">
                    <PenLine className="h-3 w-3" />
                    Signature required
                  </Badge>
                )}
              </div>
              <p className="mt-1.5 text-xs text-zinc-500">
                {q} question{q === 1 ? "" : "s"} · sent {sent} time{sent === 1 ? "" : "s"} ·{" "}
                {t.completed_count ?? 0} completed
              </p>
              <button
                type="button"
                onClick={() => onLink(t)}
                className={cn(
                  "mt-1.5 inline-flex items-center gap-1 rounded text-xs font-medium",
                  (t.service_count ?? 0) > 0 ? "text-indigo-300 hover:text-indigo-200" : "text-amber-300 hover:text-amber-200",
                )}
              >
                <CalendarCheck className="h-3.5 w-3.5" />
                {(t.service_count ?? 0) > 0
                  ? `Goes out with ${t.service_count} appointment type${t.service_count === 1 ? "" : "s"}`
                  : "Not sent with any appointment yet. Choose which ones"}
              </button>
              {t.description && (
                <p className="mt-1 max-w-xl text-xs text-zinc-500">{t.description}</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<CalendarCheck className="h-3.5 w-3.5" />}
                onClick={() => onLink(t)}
              >
                Appointment types
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Pencil className="h-3.5 w-3.5" />}
                onClick={() => onEdit(t)}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                loading={archivingId === t.id}
                leftIcon={<Archive className="h-3.5 w-3.5" />}
                onClick={() => void archive(t)}
              >
                Archive
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* ─── Which appointment types send a form ─────────────────────────────────── */

/**
 * Her menu is 160+ treatments, and which paperwork goes with which is the
 * whole reason she keeps look-alike appointment types. Tick treatments (or a
 * whole category) and say whether the form goes the first time only or
 * every visit. Saves the same links the service editor shows.
 */
function FormServicesModal({
  form, onClose, onSaved,
}: {
  form: FormTemplate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [services, setServices] = useState<Service[] | null>(null);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [picked, setPicked] = useState<Record<string, FormFrequency>>({});
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  // A newly ticked treatment gets this; the Visit Update form repeats.
  const defaultFreq: FormFrequency = form.slug === "visit-update" ? "every_visit" : "once";

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get<{ services: Service[] }>("/services"),
      api.get<{ categories: ServiceCategory[] }>("/services/categories"),
      api.get<{ links: { service_id: string; frequency: FormFrequency }[] }>(`/forms/${form.id}/services`),
    ]).then(([svc, cats, links]) => {
      if (cancelled) return;
      setServices(svc.services.filter((sv) => !sv.archived));
      setCategories(cats.categories);
      setPicked(Object.fromEntries(links.links.map((l) => [l.service_id, l.frequency])));
    }).catch((e) => toast.error("Couldn't load your appointment types", errorMessage(e)));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (services ?? []).filter((sv) => !q || sv.name.toLowerCase().includes(q));
    const order = [...categories].sort((a, b) => a.position - b.position);
    const out: { id: string; name: string; items: Service[] }[] = [];
    for (const c of order) {
      const items = list.filter((sv) => sv.category_id === c.id);
      if (items.length) out.push({ id: c.id, name: c.name, items });
    }
    const loose = list.filter((sv) => !sv.category_id || !categories.some((c) => c.id === sv.category_id));
    if (loose.length) out.push({ id: "__none__", name: "No category", items: loose });
    return out;
  }, [services, categories, query]);

  const toggle = (id: string) => setPicked((p) => {
    const next = { ...p };
    if (next[id]) delete next[id]; else next[id] = defaultFreq;
    return next;
  });
  const toggleGroup = (items: Service[], on: boolean) => setPicked((p) => {
    const next = { ...p };
    for (const sv of items) {
      if (on) next[sv.id] = next[sv.id] ?? defaultFreq;
      else delete next[sv.id];
    }
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/forms/${form.id}/services`, {
        links: Object.entries(picked).map(([service_id, frequency]) => ({ service_id, frequency })),
      });
      toast.success(`${form.name} saved`);
      onSaved();
    } catch (e) {
      toast.error("Couldn't save", errorMessage(e));
      setSaving(false);
    }
  };

  const count = Object.keys(picked).length;

  return (
    <Modal open onClose={onClose} title={`When does "${form.name}" go out?`} size="lg">
      <div className="space-y-3">
        <p className="text-xs text-zinc-500">
          Tick every appointment type that should send this form when it&rsquo;s booked. &ldquo;First time only&rdquo; skips anyone who has already completed it; &ldquo;Every visit&rdquo; sends it each time.
        </p>
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search appointment types…" />
        {services === null ? (
          <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : (
          <div className="max-h-[55vh] space-y-4 overflow-y-auto pr-1">
            {groups.map((g) => {
              const on = g.items.filter((sv) => picked[sv.id]).length;
              return (
                <div key={g.id}>
                  <label className="mb-1 flex cursor-pointer items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-indigo-500"
                      checked={on === g.items.length}
                      ref={(el) => { if (el) el.indeterminate = on > 0 && on < g.items.length; }}
                      onChange={(e) => toggleGroup(g.items, e.target.checked)}
                    />
                    {g.name}
                    <span className="font-normal normal-case tracking-normal text-zinc-600">{on}/{g.items.length}</span>
                  </label>
                  <div className="space-y-1 pl-6">
                    {g.items.map((sv) => (
                      <div key={sv.id} className="flex items-center gap-2">
                        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm text-zinc-300">
                          <input type="checkbox" className="h-4 w-4 accent-indigo-500" checked={!!picked[sv.id]} onChange={() => toggle(sv.id)} />
                          <span className="truncate">{sv.name}</span>
                        </label>
                        {picked[sv.id] && (
                          <Select
                            value={picked[sv.id]}
                            onChange={(e) => setPicked((p) => ({ ...p, [sv.id]: e.target.value as FormFrequency }))}
                            className="h-7 w-auto shrink-0 text-xs"
                            aria-label={`How often ${sv.name} sends ${form.name}`}
                          >
                            <option value="once">First time only</option>
                            <option value="every_visit">Every visit</option>
                          </Select>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {groups.length === 0 && <p className="py-6 text-center text-sm text-zinc-500">Nothing matches that search.</p>}
          </div>
        )}
      </div>
      <ModalFooter>
        <span className="mr-auto text-xs text-zinc-500">{count} selected</span>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={saving} disabled={services === null} onClick={() => void save()}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}

/* ─── Builder drawer ───────────────────────────────────────────────────────── */

const ADD_FIELD_BUTTONS: { type: FormFieldType; label: string }[] = [
  { type: "heading",     label: "Heading" },
  { type: "statement",   label: "Statement" },
  { type: "text",        label: "Short answer" },
  { type: "textarea",    label: "Long answer" },
  { type: "select",      label: "Dropdown" },
  { type: "multiselect", label: "Checkboxes" },
  { type: "checkbox",    label: "Single checkbox" },
  { type: "date",        label: "Date" },
  { type: "phone",       label: "Phone" },
  { type: "email",       label: "Email" },
  { type: "number",      label: "Number" },
];

function TemplateBuilder({
  template,
  onClose,
  onSaved,
}: {
  template: FormTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  const [name, setName] = useState(template?.name ?? "");
  const [kind, setKind] = useState<FormTemplate["kind"]>(template?.kind ?? "intake");
  const [section, setSection] = useState<string>(template?.section ?? "other");
  const [sections, setSections] = useState<FormSection[]>([]);
  useEffect(() => {
    let cancelled = false;
    api.get<{ sections: FormSection[] }>("/forms")
      .then((d) => { if (!cancelled) setSections(d.sections ?? []); })
      .catch(() => { /* the field just falls back to the current value */ });
    return () => { cancelled = true; };
  }, []);
  const [description, setDescription] = useState(template?.description ?? "");
  const [requireSignature, setRequireSignature] = useState(template?.require_signature ?? false);
  // "New patients get initial documents sent" — every form in a folder goes
  // out with a booking unless she takes it out of the rotation here.
  const [autoSend, setAutoSend] = useState(template?.auto_send ?? true);
  // "some forms are only once … Like hippa, Demographic … unless they never
  // completed the forms then will send again."
  const [onceOnly, setOnceOnly] = useState(template?.once_per_client ?? false);
  const [fields, setFields] = useState<FormField[]>(template?.fields ?? []);
  // Raw comma-separated options text per field id, so typing commas is painless.
  const [optionsText, setOptionsText] = useState<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const f of template?.fields ?? []) {
      if (f.type === "select" || f.type === "multiselect") map[f.id] = (f.options ?? []).join(", ");
    }
    return map;
  });
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const updateField = (id: string, patch: Partial<FormField>) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const moveField = (index: number, dir: -1 | 1) => {
    setFields((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeField = (id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  };

  const addField = (type: FormFieldType) => {
    const id = `f_${Date.now().toString(36)}`;
    const field: FormField = { id, type, label: "" };
    if (type === "select" || type === "multiselect") {
      setOptionsText((prev) => ({ ...prev, [id]: "" }));
    }
    setFields((prev) => [...prev, field]);
  };

  const save = async () => {
    setValidationError(null);

    // Fold the raw options text back into the fields payload.
    const finalFields: FormField[] = fields.map((f) => {
      if (f.type === "select" || f.type === "multiselect") {
        const options = (optionsText[f.id] ?? "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        return { ...f, options };
      }
      return f;
    });

    if (!name.trim()) {
      setValidationError("Give the form a name.");
      return;
    }
    const missingLabel = finalFields.find((f) => f.type !== "heading" && !f.label.trim());
    if (missingLabel) {
      setValidationError(`Every ${FIELD_TYPE_LABELS[missingLabel.type].toLowerCase()} field needs a label.`);
      return;
    }
    const missingOptions = finalFields.find(
      (f) => (f.type === "select" || f.type === "multiselect") && (f.options ?? []).length === 0
    );
    if (missingOptions) {
      setValidationError(`"${missingOptions.label || FIELD_TYPE_LABELS[missingOptions.type]}" needs at least one option.`);
      return;
    }

    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        kind,
        section,
        description: description.trim() || null,
        fields: finalFields,
        require_signature: requireSignature,
        auto_send: autoSend,
        once_per_client: onceOnly,
      };
      if (template) {
        await api.patch(`/forms/${template.id}`, body);
        toast.success("Form updated");
      } else {
        await api.post("/forms", body);
        toast.success("Form created");
      }
      onSaved();
    } catch (e) {
      toast.error("Couldn't save form", errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open onClose={onClose} width="xl" title={template ? "Edit form" : "New form"}>
      <div className="space-y-5 p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label required htmlFor="form-name">Name</Label>
            <Input
              id="form-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="New client intake"
            />
          </div>
          <div>
            <Label htmlFor="form-kind">Kind</Label>
            <Select
              id="form-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as FormTemplate["kind"])}
            >
              <option value="intake">Intake</option>
              <option value="consent">Consent</option>
              <option value="custom">Custom</option>
            </Select>
          </div>
        </div>

        {/* Which folder a completed copy files itself into on the client's
            record — Renewal sits at the top, then the initial packets. */}
        <div>
          <Label htmlFor="form-section">Paperwork folder</Label>
          <Select id="form-section" value={section} onChange={(e) => setSection(e.target.value)}>
            {(sections.length > 0 ? sections : [{ key: section, label: section, blurb: "" }]).map((sct) => (
              <option key={sct.key} value={sct.key}>{sct.label}</option>
            ))}
          </Select>
          <FieldHint>
            Completed copies file themselves here on the client's record, so the initial packet and the every-visit renewal never get mixed up.
          </FieldHint>
          <label className="mt-2 flex cursor-pointer items-start gap-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              data-auto-send
              checked={autoSend}
              onChange={(e) => setAutoSend(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
            />
            <span>
              Send this automatically when a visit is booked
              <span className="block text-[11px] text-zinc-500">
                Untick to keep it in the folder but only send it by hand.
              </span>
            </span>
          </label>
          <label className="mt-2 flex cursor-pointer items-start gap-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              data-once-only
              checked={onceOnly}
              onChange={(e) => setOnceOnly(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
            />
            <span>
              Only ever needed once from each client
              <span className="block text-[11px] text-zinc-500">
                For things like your privacy notice and demographics. It can sit in every folder
                and will still stop going out once they&rsquo;ve completed it - so an IV client
                who starts weight loss isn&rsquo;t asked again. If they never filled it in, it goes again.
              </span>
            </span>
          </label>
        </div>

        <div>
          <Label htmlFor="form-description">Description</Label>
          <Textarea
            id="form-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Shown to your team only — what is this form for?"
            rows={2}
          />
        </div>

        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            checked={requireSignature}
            onChange={(e) => setRequireSignature(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500"
          />
          <span>
            <span className="block text-sm text-zinc-200">Require signature</span>
            <span className="mt-0.5 block text-xs text-zinc-500">
              Client types their full name to sign — stored with date, time and IP.
            </span>
          </span>
        </label>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-zinc-200">Fields</h3>

          {fields.length === 0 && (
            <p className="rounded-lg border border-dashed border-zinc-800 px-4 py-6 text-center text-xs text-zinc-500">
              No fields yet — add your first question below.
            </p>
          )}

          {fields.map((f, i) => {
            const isInput = INPUT_TYPES.includes(f.type);
            const hasOptions = f.type === "select" || f.type === "multiselect";
            const hasPlaceholder = PLACEHOLDER_TYPES.includes(f.type);
            return (
              <div key={f.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => moveField(i, -1)}
                      disabled={i === 0}
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200 disabled:opacity-30 disabled:hover:bg-transparent"
                      aria-label="Move field up"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveField(i, 1)}
                      disabled={i === fields.length - 1}
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200 disabled:opacity-30 disabled:hover:bg-transparent"
                      aria-label="Move field down"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="min-w-0 flex-1 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
                        {FIELD_TYPE_LABELS[f.type]}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeField(f.id)}
                        className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800/60 hover:text-rose-400"
                        aria-label="Delete field"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    <div>
                      <Label required={f.type !== "heading"}>Label</Label>
                      <Input
                        value={f.label}
                        onChange={(e) => updateField(f.id, { label: e.target.value })}
                        placeholder={f.type === "heading" ? "Section title" : "Question"}
                      />
                    </div>

                    {f.type === "statement" && (
                      <div>
                        <Label>Text shown to the client</Label>
                        <Textarea
                          value={f.text ?? ""}
                          onChange={(e) => updateField(f.id, { text: e.target.value })}
                          rows={3}
                        />
                      </div>
                    )}

                    {hasOptions && (
                      <div>
                        <Label>Options (comma-separated)</Label>
                        <Input
                          value={optionsText[f.id] ?? ""}
                          onChange={(e) =>
                            setOptionsText((prev) => ({ ...prev, [f.id]: e.target.value }))
                          }
                          placeholder="Option one, Option two, Option three"
                        />
                      </div>
                    )}

                    {hasPlaceholder && (
                      <div>
                        <Label>Placeholder</Label>
                        <Input
                          value={f.placeholder ?? ""}
                          onChange={(e) => updateField(f.id, { placeholder: e.target.value })}
                          placeholder="Optional hint text"
                        />
                      </div>
                    )}

                    {isInput && (
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!f.required}
                          onChange={(e) => updateField(f.id, { required: e.target.checked })}
                          className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500"
                        />
                        <span className="text-xs text-zinc-300">Required</span>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <div>
            <p className="mb-1.5 text-xs font-medium text-zinc-400">Add field</p>
            <div className="flex flex-wrap gap-1.5">
              {ADD_FIELD_BUTTONS.map((b) => (
                <Button
                  key={b.type}
                  variant="outline"
                  size="sm"
                  leftIcon={<Plus className="h-3 w-3" />}
                  onClick={() => addField(b.type)}
                >
                  {b.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {validationError && <FieldHint error>{validationError}</FieldHint>}

        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 pt-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={() => void save()}>
            {template ? "Save changes" : "Create form"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

/* ─── Sent tab ─────────────────────────────────────────────────────────────── */

type SentFilter = "" | "sent" | "viewed" | "completed";

function SentTab({ reloadKey, onChanged }: { reloadKey: number; onChanged: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();

  const [filter, setFilter] = useState<SentFilter>("");
  const [requests, setRequests] = useState<FormRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<FormRequest | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.get<{ requests: FormRequest[] }>("/forms/requests", {
        status: filter || undefined,
        limit: 100,
      });
      setRequests(r.requests);
    } catch (e) {
      setError(errorMessage(e));
      setRequests((prev) => prev ?? []);
    }
  }, [filter]);

  useEffect(() => { setRequests(null); void load(); }, [load, reloadKey]);

  const copyLink = async (r: FormRequest) => {
    try {
      await navigator.clipboard.writeText(r.url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  const resend = async (r: FormRequest) => {
    setBusyId(r.id);
    try {
      await api.post(`/forms/requests/${r.id}/resend`);
      toast.success("Form re-sent");
      void load();
    } catch (e) {
      toast.error("Couldn't resend", errorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r: FormRequest) => {
    const ok = await confirm({
      title: "Delete this form request?",
      description: "The link stops working and the request disappears from this list.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    setBusyId(r.id);
    try {
      await api.delete(`/forms/requests/${r.id}`);
      toast.success("Request deleted");
      onChanged();
      void load();
    } catch (e) {
      toast.error("Couldn't delete", errorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="w-44">
          <Select value={filter} onChange={(e) => setFilter(e.target.value as SentFilter)}>
            <option value="">All statuses</option>
            <option value="sent">Awaiting</option>
            <option value="viewed">Opened</option>
            <option value="completed">Completed</option>
          </Select>
        </div>
      </div>

      {requests === null ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : error && requests.length === 0 ? (
        <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5 text-center">
          <p className="text-sm text-rose-400">{error}</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => { setRequests(null); void load(); }}>
            Try again
          </Button>
        </div>
      ) : requests.length === 0 ? (
        <EmptyState
          icon={Send}
          title={filter ? "Nothing matches this filter" : "No forms sent yet"}
          description={
            filter
              ? "Try a different status filter."
              : "Send a form to a client and it shows up here with its status."
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800/70 bg-zinc-900/40 text-xs uppercase tracking-wider text-zinc-500">
                  <th className="px-3 py-2.5 text-left font-medium">Client</th>
                  <th className="px-3 py-2.5 text-left font-medium">Form</th>
                  <th className="px-3 py-2.5 text-left font-medium">Status</th>
                  <th className="px-3 py-2.5 text-left font-medium">Sent</th>
                  <th className="px-3 py-2.5 text-left font-medium">Completed</th>
                  <th className="px-3 py-2.5 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => {
                  const status = STATUS_BADGE[r.status] ?? STATUS_BADGE.sent;
                  const kindLabel =
                    r.template_kind && KIND_BADGE[r.template_kind as FormTemplate["kind"]]
                      ? KIND_BADGE[r.template_kind as FormTemplate["kind"]].label
                      : null;
                  const completed = r.status === "completed";
                  return (
                    <tr key={r.id} className="border-b border-zinc-800/40 last:border-b-0 hover:bg-zinc-900/30">
                      <td className="px-3 py-2.5">
                        <Link
                          to={`/contacts/${r.contact_id}`}
                          state={{ from: "/forms" }}
                          className="font-medium text-zinc-100 hover:text-indigo-300"
                        >
                          {r.contact_name || "Unnamed contact"}
                        </Link>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-zinc-200">{r.template_name || "Deleted form"}</span>
                        {kindLabel && <span className="ml-1.5 text-xs text-zinc-500">{kindLabel}</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge color={status.color}>{status.label}</Badge>
                      </td>
                      <td className="px-3 py-2.5 text-zinc-400" title={formatDate(r.sent_at)}>
                        {formatRelative(r.sent_at)}
                      </td>
                      <td className="px-3 py-2.5 text-zinc-400">
                        {r.completed_at ? formatDate(r.completed_at) : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            type="button"
                            onClick={() => void copyLink(r)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
                            title="Copy link"
                            aria-label="Copy link"
                          >
                            <Clipboard className="h-3.5 w-3.5" />
                          </button>
                          {completed && (
                            <button
                              type="button"
                              onClick={() => setViewing(r)}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
                              title="View answers"
                              aria-label="View answers"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {!completed && (
                            <button
                              type="button"
                              onClick={() => void resend(r)}
                              disabled={busyId === r.id}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200 disabled:opacity-40"
                              title="Resend"
                              aria-label="Resend"
                            >
                              <RefreshCw className={cn("h-3.5 w-3.5", busyId === r.id && "animate-spin")} />
                            </button>
                          )}
                          {!completed && (
                            <button
                              type="button"
                              onClick={() => void remove(r)}
                              disabled={busyId === r.id}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800/60 hover:text-rose-400 disabled:opacity-40"
                              title="Delete"
                              aria-label="Delete"
                            >
                              <Trash className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AnswersModal request={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

/* ─── View-answers modal ───────────────────────────────────────────────────── */

function AnswersModal({ request, onClose }: { request: FormRequest | null; onClose: () => void }) {
  return (
    <Modal
      open={request !== null}
      onClose={onClose}
      size="lg"
      title={request?.template_name || "Form answers"}
      description={request?.contact_name ? `Completed by ${request.contact_name}` : undefined}
    >
      {request && (
        <div className="max-h-[60vh] space-y-4 overflow-y-auto">
          {request.fields.map((f) => {
            if (f.type === "statement") return null;
            if (f.type === "heading") {
              return (
                <h3
                  key={f.id}
                  className="border-b border-zinc-800 pb-1.5 pt-2 text-sm font-semibold text-zinc-100 first:pt-0"
                >
                  {f.label}
                </h3>
              );
            }
            if (f.type === "diagram") {
              return (
                <div key={f.id}>
                  <p className="text-xs font-medium text-zinc-500">{f.label}</p>
                  <DiagramAnswer value={request.answers?.[f.id]} which={f.options?.[0] ?? "face-front"} />
                </div>
              );
            }
            return (
              <div key={f.id}>
                <p className="text-xs font-medium text-zinc-500">{f.label}</p>
                <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-200">
                  {renderAnswer(request.answers?.[f.id])}
                </p>
              </div>
            );
          })}

          {request.signature && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3">
              <p className="flex items-center gap-2 text-sm text-amber-200">
                <PenLine className="h-4 w-4 shrink-0" />
                Signed by {request.signature.name} on {formatDate(request.signature.signed_at)}
              </p>
            </div>
          )}
        </div>
      )}
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}

/* ─── Send-a-form dialog ───────────────────────────────────────────────────── */

function SendFormDialog({
  open,
  templates,
  onClose,
  onSent,
}: {
  open: boolean;
  templates: FormTemplate[];
  onClose: () => void;
  onSent: () => void;
}) {
  const toast = useToast();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Contact[]>([]);
  const [searching, setSearching] = useState(false);
  const [contact, setContact] = useState<Contact | null>(null);
  const [templateId, setTemplateId] = useState("");
  const [sending, setSending] = useState(false);

  // Reset state each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setResults([]);
    setContact(null);
    setTemplateId(templates[0]?.id ?? "");
    setSending(false);
  }, [open, templates]);

  // Debounced contact search (250ms).
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get<{ contacts: Contact[] }>("/contacts", { q, limit: 8 });
        setResults(r.contacts);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  const send = async () => {
    if (!contact || !templateId) return;
    setSending(true);
    try {
      const r = await api.post<{
        requestId: string;
        url: string;
        delivered: boolean;
        channel: string;
        error?: string;
      }>("/forms/requests", { form_template_id: templateId, contact_id: contact.id });
      if (r.delivered) {
        toast.success(`Form sent by ${r.channel}`);
      } else {
        toast.error(r.error ?? "Created, but couldn't deliver — copy the link instead");
      }
      onSent();
    } catch (e) {
      toast.error("Couldn't send the form", errorMessage(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="md" title="Send a form">
      <div className="space-y-4">
        <div>
          <Label required>Client</Label>
          {contact ? (
            <div className="flex items-center gap-2">
              <Badge color="indigo" size="md" onRemove={() => setContact(null)}>
                {contactName(contact)}
              </Badge>
            </div>
          ) : (
            <div className="relative">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search clients by name, email or phone…"
                autoFocus
              />
              {query.trim() && (
                <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl shadow-black/40">
                  {searching ? (
                    <p className="px-3 py-2.5 text-xs text-zinc-500">Searching…</p>
                  ) : results.length === 0 ? (
                    <p className="px-3 py-2.5 text-xs text-zinc-500">No clients found.</p>
                  ) : (
                    <ul className="max-h-56 overflow-y-auto">
                      {results.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            onClick={() => { setContact(c); setQuery(""); setResults([]); }}
                            className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-zinc-900"
                          >
                            <span className="text-sm text-zinc-100">{contactName(c)}</span>
                            {(c.email || c.phone) && (
                              <span className="text-xs text-zinc-500">{c.email || c.phone}</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <Label required>Form</Label>
          <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            {templates.length === 0 && <option value="">No forms available</option>}
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({KIND_BADGE[t.kind]?.label ?? "Custom"})
              </option>
            ))}
          </Select>
          {templates.length === 0 && (
            <FieldHint>Create a form first, then come back to send it.</FieldHint>
          )}
        </div>
      </div>

      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button
          loading={sending}
          disabled={!contact || !templateId}
          leftIcon={<Send className="h-4 w-4" />}
          onClick={() => void send()}
        >
          Send
        </Button>
      </ModalFooter>
    </Modal>
  );
}
